import axios, { AxiosError, AxiosRequestConfig, AxiosHeaders } from 'axios'
import { storage } from '@/lib/storage'

export const DlInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  // withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies
})

let isRefreshing = false
let failedQueue: any[] = []

const processQueue = (error: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })
  failedQueue = []
}

// Add a request interceptor to add the token to all requests
DlInstance.interceptors.request.use(
  (config) => {
    const token = storage.get('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Add a response interceptor to handle errors
DlInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest?.url?.includes('/auth/refresh')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => DlInstance(originalRequest!))
          .catch((err) => Promise.reject(err))
      }

      isRefreshing = true

      try {
        const refreshToken = storage.get('refreshToken')
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        const response = await DlInstance.post('/auth/refresh', {
          refreshToken,
        })

        const { accessToken, refreshToken: newRefreshToken } = response.data
        storage.set('accessToken', accessToken)
        storage.set('refreshToken', newRefreshToken)

        processQueue()

        if (originalRequest) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return DlInstance(originalRequest)
        }
      } catch (refreshError) {
        processQueue(refreshError)
        storage.remove('accessToken')
        storage.remove('refreshToken')
        storage.remove('userRole')
        window.location.href = window.location.pathname.startsWith('/liff')
          ? '/liff/login'
          : '/auth/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

export const DlAdminInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL + '/admin',
  // withCredentials: true,
})

export const Instance = {
  default: DlInstance,
  admin: DlAdminInstance,
}
